import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { getPublicCompany, PublicCompany } from "../../lib/publicApi";

export default function PublicLayout() {
  const [company, setCompany] = useState<PublicCompany | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    getPublicCompany().then(setCompany).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Zavři mobilní menu při navigaci
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Zablokuj scroll body, když je menu otevřené
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <div className="bg-obloha-indigo-950 text-white">
      <header
        className={`sticky top-0 z-30 transition-all duration-300 ${
          scrolled ? "bg-obloha-indigo-950/80 backdrop-blur-xl shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-xl font-semibold tracking-wide">
            {company?.logoUrl ? (
              <img
                src={company.logoUrl}
                alt="Kouzelná Obloha"
                className="h-16 w-auto max-w-[280px] object-contain md:h-20"
              />
            ) : (
              <>
                <span className="text-2xl">🌌</span>
                <span>Kouzelná Obloha</span>
              </>
            )}
          </Link>
          <nav className="hidden items-center gap-8 text-sm font-medium md:flex">
            <NavItem to="/" end>
              Úvod
            </NavItem>
            <NavItem to="/programy">Programy</NavItem>
            <NavItem to="/kontakt">Kontakt</NavItem>
            <Link
              to="/rezervace"
              className="rounded-full bg-gradient-to-r from-obloha-gold-500 to-obloha-gold-400 px-6 py-2.5 font-semibold text-obloha-indigo-950 shadow-lg shadow-obloha-gold-500/30 transition-all hover:scale-105 hover:shadow-xl hover:shadow-obloha-gold-500/50"
            >
              Rezervovat
            </Link>
          </nav>
          {/* Mobilní pravá strana: CTA + hamburger */}
          <div className="flex items-center gap-2 md:hidden">
            <Link
              to="/rezervace"
              className="rounded-full bg-obloha-gold-500 px-4 py-2 text-sm font-semibold text-obloha-indigo-950"
            >
              Rezervovat
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((o) => !o)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition hover:bg-white/10"
              aria-label={mobileOpen ? "Zavřít menu" : "Otevřít menu"}
              aria-expanded={mobileOpen}
            >
              <svg
                className="h-5 w-5 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobilní rozbalovací menu */}
        {mobileOpen && (
          <div className="border-t border-white/10 bg-obloha-indigo-950/95 backdrop-blur-xl md:hidden">
            <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4 text-base font-medium">
              <MobileLink to="/" end>Úvod</MobileLink>
              <MobileLink to="/programy">Programy</MobileLink>
              <MobileLink to="/kontakt">Kontakt</MobileLink>
              <Link
                to="/rezervace"
                className="mt-2 rounded-full bg-gradient-to-r from-obloha-gold-500 to-obloha-gold-400 py-3 text-center font-semibold text-obloha-indigo-950 shadow-lg shadow-obloha-gold-500/30"
              >
                ✨ Rezervovat termín
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="relative border-t border-white/10 bg-obloha-indigo-950">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="grid gap-8 md:grid-cols-3">
            <div>
              <div className="mb-3 flex items-center gap-2 text-xl font-semibold">
                {company?.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt="Kouzelná Obloha"
                    className="h-24 w-auto max-w-[320px] object-contain"
                  />
                ) : (
                  <>
                    <span className="text-2xl">🌌</span>
                    <span>Kouzelná Obloha</span>
                  </>
                )}
              </div>
              <p className="max-w-md text-sm text-slate-300">
                Mobilní sférické planetárium pro mateřské školky po celé ČR. Přijedeme,
                postavíme, odehrajeme, sbalíme — a vaše děti na to nikdy nezapomenou.
              </p>
            </div>

            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-obloha-gold-400">
                Menu
              </div>
              <ul className="space-y-2 text-sm text-slate-300">
                <li>
                  <Link to="/" className="hover:text-white">
                    Úvod
                  </Link>
                </li>
                <li>
                  <Link to="/programy" className="hover:text-white">
                    Programy
                  </Link>
                </li>
                <li>
                  <Link to="/rezervace" className="hover:text-white">
                    Rezervace termínu
                  </Link>
                </li>
                <li>
                  <Link to="/kontakt" className="hover:text-white">
                    Kontakt
                  </Link>
                </li>
              </ul>
            </div>

            {company && company.name && (
              <div>
                <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-obloha-gold-400">
                  Kontakt
                </div>
                <address className="text-sm not-italic leading-relaxed text-slate-300">
                  <div className="font-medium text-white">{company.name}</div>
                  {company.street && (
                    <div>
                      {company.street}, {company.zip} {company.city}
                    </div>
                  )}
                  {company.ico && <div className="text-slate-400">IČO: {company.ico}</div>}
                  {company.email && (
                    <div className="mt-2">
                      <a href={`mailto:${company.email}`} className="hover:text-obloha-gold-400">
                        {company.email}
                      </a>
                    </div>
                  )}
                  {company.phone && (
                    <div>
                      <a href={`tel:${company.phone}`} className="hover:text-obloha-gold-400">
                        {company.phone}
                      </a>
                    </div>
                  )}
                </address>
              </div>
            )}
          </div>

          <div className="mt-10 border-t border-white/10 pt-6 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} Kouzelná Obloha · kouzelnaobloha.cz
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavItem({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `transition-colors ${
          isActive ? "text-obloha-gold-400" : "text-slate-200 hover:text-white"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function MobileLink({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `rounded-lg px-4 py-3 transition-colors ${
          isActive
            ? "bg-obloha-gold-500/10 text-obloha-gold-400"
            : "text-slate-200 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
